// src/pages/admin/ReceiptForm.jsx
import logger from '../../utils/logger';
import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createInventoryReceipt,
  searchActivityProductsForReceipt,
  searchDistributorProductsForReceipt,
} from "../../services/inventoryReceiptApi";
import Modal from '../../components/Modal';
import {
  PageContainer,
  PageTitle,
  ActionButton,
  Input,
  Select,
  NumpadGrid,
  KeyReverse,
  ErrorText,
  FormReceipt
} from '../../ui';
import {
  calculate_unit_ht_from_ttc,

} from '../../utils/tax_utils';
import { useAdminScope } from "../../context/AdminScopeContext";

export default function ReceiptForm() {
  const navigate = useNavigate();

  // État formulaire
  const [form, setForm] = useState({
    supplier_reference: '',
    ean: '',
    reference: '',
    quantity: '',
    unit_cost_ht: '',
    unit_cost_ttc: '',
    vat_rate: 0.20
  });
  const [activeField, setActiveField] = useState(null);
  const [error, setError] = useState('');
  const { selectedCompanyUuid } = useAdminScope();

  // Modal distributeur
  const [showDistModal, setShowDistModal] = useState(false);
  const [distCandidates, setDistCandidates] = useState([]);
  const distributorSelectRef = useRef(null);
  const vatChoices = [0.20, 0.10, 0.055];

  // Gestion saisie générale
  const onChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  // Pavé numérique
  const appendKey = k => {
    if (!activeField) return;
    setForm(f => ({
      ...f,
      [activeField]: (f[activeField] + k)
    }));
  };
  const backspace = () => {
    if (!activeField) return;
    setForm(f => ({
      ...f,
      [activeField]: f[activeField].slice(0, -1)
    }));
  };
  const submitViaKey = () => {
    handleSubmit();
  };

  // Lookup automatique EAN / Réf
  const handleLookup = async () => {
    setError('');
    const q = form.ean.trim() || form.reference.trim();
    if (!q) return;
    try {
      // recherche en stock
      const productRes = await searchActivityProductsForReceipt(q, {
        companyUuid: selectedCompanyUuid,
      });
      const found = productRes.data.results.find(
        i => String(i.ean) === q || i.reference === q
      );
      if (found) {
        const ttc = String(found.purchased_brut ?? found.selling_price ?? 0);
        const ht = String(
          calculate_unit_ht_from_ttc(ttc, (found.vat_rate ?? 20) / 100)
        );
        setForm(f => ({
          ...f,
          unit_cost_ttc: ttc,
          unit_cost_ht: ht,
          vat_rate: (found.vat_rate ?? 20) / 100
        }));
        return;
      }
      // sinon distributeur
      const distRes = await searchDistributorProductsForReceipt(q, {
        companyUuid: selectedCompanyUuid,
      });
      if (distRes.data.results.length) {
        setDistCandidates(distRes.data.results);
        setShowDistModal(true);
      }
    } catch (err) {
      logger.error(err);
      setError('Erreur lookup produit');
    }
  };

  // Soumission finale
  const handleSubmit = async () => {
    setError('');
    try {
      // conversion numérique
      const payload = {
        ...form,
        quantity: Number(form.quantity),
        unit_cost_ttc: Number(form.unit_cost_ttc),
        unit_cost_ht: Number(form.unit_cost_ht),
        vat_rate: form.vat_rate
      };
      await createInventoryReceipt(payload, {
        companyUuid: selectedCompanyUuid,
      });
      navigate('/admin/inventory/receipts');
    } catch (err) {
      logger.error(err);
      setError(err.response?.data?.error || 'Impossible de créer la réception');
    }
  };

  return (
    <PageContainer>
      <PageTitle>Nouvelle réception</PageTitle>
      {error && <ErrorText>{error}</ErrorText>}
      <FormReceipt onSubmit={handleSubmit}>

      <Input
        label="Réf. fournisseur"
        name="supplier_reference"
        value={form.supplier_reference}
        onChange={onChange}
        placeholder="Réf. fournisseur"
      />
      <Input
        label="EAN"
        name="ean"
        value={form.ean}
        onChange={onChange}
        onBlur={handleLookup}
        placeholder="EAN"
      />
      <Input
        label="Référence produit"
        name="reference"
        value={form.reference}
        onChange={onChange}
        onBlur={handleLookup}
        placeholder="Référence produit"
      />
      <Input
        label="Quantité"
        name="quantity"
        value={form.quantity}
        onChange={onChange}
        type="text"
        onFocus={() => setActiveField('quantity')}
        placeholder="Quantité"
      />
      <Input
        label="Coût unitaire TTC"
        name="unit_cost_ttc"
        value={form.unit_cost_ttc}
        onChange={onChange}
        type="text"
        onFocus={() => setActiveField('unit_cost_ttc')}
        placeholder="Coût unitaire TTC"
      />
      <Input
        label="Coût unitaire HT"
        name="unit_cost_ht"
        value={form.unit_cost_ht}
        onChange={onChange}
        type="text"
        onFocus={() => setActiveField('unit_cost_ht')}
        placeholder="Coût unitaire HT"
      />
      <section>Sélectionner le taux de TVA</section>
      <Select
        label="TVA"
        name="vat_rate"
        value={form.vat_rate}
        onChange={e =>
          setForm(f => ({ ...f, vat_rate: parseFloat(e.target.value) }))
        }
      >
        {vatChoices.map(v => (
          <option key={v} value={v}>
            {(v * 100).toFixed(0)} %
          </option>
        ))}
      </Select>

      {/* Pavé numérique droit */}
      <NumpadGrid className="numpad-grid">
        {['1','2','3','4','5','6','7','8','9','←','0','✔'].map(k => (
          <KeyReverse
            key={k}
            onClick={() => {
              if (k === '←') backspace();
              else if (k === '✔') submitViaKey();
              else appendKey(k);
            }}
          >
            {k}
          </KeyReverse>
        ))}
      </NumpadGrid>

      </FormReceipt>
      <div style={{ marginTop: '2rem' }}>
        <ActionButton onClick={handleSubmit}>Enregistrer</ActionButton>
        <ActionButton onClick={() => navigate(-1)}>Annuler</ActionButton>
      </div>


      
      {/* Modal distributeur */}
      {showDistModal && (
        <Modal
          onClose={() => setShowDistModal(false)}
          role="dialog"
          ariaLabelledBy="receipt-distributor-selection-title"
          ariaDescribedBy="receipt-distributor-selection-description"
          initialFocusRef={distributorSelectRef}
          closeOnOverlay={false}
        >
          <div style={{ padding: '1rem' }}>
            <h2 id="receipt-distributor-selection-title">
              Produit non trouvé en stock
            </h2>
            <p id="receipt-distributor-selection-description">
              Choisissez une référence à créer :
            </p>
            <select
              ref={distributorSelectRef}
              aria-label="Référence distributeur"
              defaultValue=""
              onChange={e => {
                const sel = distCandidates.find(d => d.uuid === e.target.value);
                if (!sel) return;
                setForm(f => ({
                  ...f,
                  ean: sel.ean,
                  reference: sel.reference,
                  unit_cost_ttc: '',
                  unit_cost_ht: '',
                  vat_rate: vatChoices[0]
                }));
                setShowDistModal(false);
              }}
            >
              <option value="">-- choisir --</option>
              {distCandidates.map(d => (
                <option key={d.uuid} value={d.uuid}>
                  {d.designation} ({d.reference})
                </option>
              ))}
            </select>
          </div>
        </Modal>
      )}
    </PageContainer>
  );
}
